// How a collection (one collector run) reads in words (ENTRAID-347 redesign, Data collection page, mockup 3). One
// vocabulary for every page that shows a collection: Complete, Partial, Timed out, Failed, each with a status dot
// tone. Status words are ink text; color lives only in the dot. Also the per-day grid behind "Collections, last 30
// days", built on the same local calendar days as the score history (lib/score-history.js).

import { COLLECTOR_CATEGORIES, licenseRequirement } from '../../../shared/collectors.mjs';
import { localDayKey, daysBetween } from './score-history.js';

const finite = (n) => typeof n === 'number' && Number.isFinite(n);

const META = (() => {
  const map = {};
  for (const cat of COLLECTOR_CATEGORIES) for (const c of cat.collectors) map[c.id] = { label: c.label, note: c.note ?? null };
  return map;
})();

/** The source's label, or its id when the catalog does not know it. */
export const sourceLabel = (id) => META[id]?.label ?? id;
/** The source's catalog note (a known limitation), or null. */
export const sourceNote = (id) => META[id]?.note ?? null;

/** "Complete", "Partial", "Timed out" or "Failed" for a run status code. */
export function collectionWord(code) {
  if (code === 200) return 'Complete';
  if (code === 207) return 'Partial';
  if (code === 206) return 'Timed out';
  return 'Failed';
}

/** The status dot tone of a run: good, warn or bad. */
export function collectionTone(code) {
  if (code === 200) return 'good';
  if (code === 207 || code === 206) return 'warn';
  return 'bad';
}

/** Total rows of a run. */
export const runRows = (run) => Object.values(run?.rowCounts ?? {}).reduce((a, b) => a + (finite(b) ? b : 0), 0);

/** Sources with partial data (a record cap or deadline cut them short, or the watchdog abandoned them). */
export const partialSources = (run) => [...new Set([...(run?.truncated ?? []), ...(run?.incomplete ?? [])])];

/** "4 min 12 s", "45 s", or null without a duration. */
export function durationText(ms) {
  if (!finite(ms) || ms < 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s`;
}

/** What started the run: the schedule, a retry, or someone's Collect now. */
export function triggerText(run) {
  if (run?.triggerSource === 'scheduled') return 'Schedule';
  if (run?.triggerSource === 'retry') return 'Retry';
  return 'Collect now';
}

/** "Retried (attempt N of M)" for a retry attempt, "Retry scheduled" for a failed attempt with a pending retry (ENTRAID-349). */
export function retryText(run) {
  const max = run?.maxAttempts ?? 1;
  if (run?.attempt > 1) return `Retried (attempt ${run.attempt} of ${max})`;
  if (run?.retryPending === true) return 'Retry scheduled';
  return null;
}

/** Why a source did not run (run summary skipReasons), in words. */
export function skipReasonText(id, reason) {
  if (reason === 'unlicensed') return `skipped: ${(licenseRequirement(id) ?? 'license required').replace(/^Requires/, 'requires')}`;
  if (reason === 'disabled') return 'turned off';
  if (reason === 'parent-disabled' || reason === 'parent-failed') return 'skipped: the source it depends on did not run';
  if (reason === 'timeout') return 'skipped: run time limit';
  return 'skipped';
}

/**
 * The sources line of a history row: "14 collected", plus failed and skipped counts, and the partial sources by name
 * so a partial run is never mistaken for a complete one without opening the row.
 */
export function sourcesLine(run) {
  const ok = (run?.succeeded ?? []).length;
  const failed = (run?.failed ?? []).length;
  const skipped = (run?.skipped ?? []).length;
  const partial = partialSources(run);
  const parts = [`${ok} collected`];
  if (failed > 0) parts.push(`${failed} failed`);
  if (skipped > 0) parts.push(`${skipped} skipped`);
  const head = parts.join(', ');
  return partial.length > 0 ? `${head}, ${partial.length} partial: ${partial.map(sourceLabel).join(', ')}` : head;
}

const dayDate = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};
/** "14 Oct" for a day key. */
export const dayLabel = (key) => dayDate(key).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).replace('Sept', 'Sep');

/** The tile state of one run: on (complete), warn (partial or timed out) or bad (failed). */
const runState = (code) => (code === 200 ? 'on' : code === 207 || code === 206 ? 'warn' : 'bad');

/**
 * One tile per local calendar day for the last `days` days (oldest first), from the runs list. A day's state is its
 * last run (a retry that succeeded makes the day complete); a day with no run is 'off'. The window never claims days
 * before the first collection (see `fullWindow`). `windowDays` is that true length.
 *
 * @returns {{ states: Array<'on'|'off'|'warn'|'bad'>, windowDays: number, complete: number, collected: number,
 *   partialDays: string[], failedDays: string[] }}
 */
export function collectionDays(runs, { days = 30, now = Date.now(), fullWindow = false } = {}) {
  const list = Array.isArray(runs) ? runs : [];
  const today = localDayKey(now);
  const byDay = new Map();
  let oldest = null;
  for (const r of list) {
    const t = Date.parse(r?.runTimestamp ?? '');
    if (!Number.isFinite(t)) continue;
    const day = localDayKey(t);
    if (!oldest || day < oldest) oldest = day;
    const age = daysBetween(day, today);
    if (age < 0 || age >= days) continue;
    const held = byDay.get(day);
    if (!held || t > held.t) byDay.set(day, { t, code: r.statusCode });
  }
  if (!oldest) return { states: [], windowDays: 0, complete: 0, collected: 0, partialDays: [], failedDays: [] };
  // `fullWindow`: the caller knows collections exist from before the window (the customer is older than it), so the
  // whole window counts and a day without a run is a missed day. Otherwise the window starts at the oldest run the list
  // holds (the first collection ever), at most `days` back, so it never claims days before onGuard was collecting.
  const windowDays = fullWindow ? days : Math.min(days, daysBetween(oldest, today) + 1);
  const states = [];
  const partialDays = [];
  const failedDays = [];
  for (let i = windowDays - 1; i >= 0; i -= 1) {
    const d = new Date(dayDate(today));
    d.setDate(d.getDate() - i);
    const key = localDayKey(d);
    const held = byDay.get(key);
    const state = held ? runState(held.code) : 'off';
    states.push(state);
    if (state === 'warn') partialDays.push(key);
    if (state === 'bad') failedDays.push(key);
  }
  return {
    states,
    windowDays,
    complete: states.filter((s) => s === 'on').length,
    collected: states.filter((s) => s !== 'off').length,
    partialDays,
    failedDays,
  };
}

/** The caption under the 30-day tiles: the partial and failed days in words, or that every collected day completed. */
export function collectionDaysCaption(grid) {
  if (!grid || grid.windowDays === 0) return { text: 'No collection yet', tone: undefined };
  const named = (list, word) => {
    if (list.length === 0) return null;
    if (list.length <= 2) return `${list.length} ${word} on ${list.map(dayLabel).join(' and ')}`;
    return `${list.length} ${word}`;
  };
  const failed = named(grid.failedDays, 'failed');
  const partial = named(grid.partialDays, 'partial');
  if (failed || partial) return { text: [failed, partial].filter(Boolean).join(', '), tone: failed ? 'bad' : 'warn' };
  const missed = grid.windowDays - grid.collected;
  if (missed > 0) return { text: `${missed} ${missed === 1 ? 'day' : 'days'} without a collection`, tone: undefined };
  return { text: 'Every day completed', tone: 'good' };
}
